import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { StorageKeys } from 'shared/lib/storage/storageService';

type PromoState = {
  /** ISO timestamp of the most recent dismissal (any surface). */
  lastDismissedAt: string | null;
  /** Cumulative dismiss count across surfaces, drives the re-prompt cadence. */
  dismissCount: number;
  /** Once true, future eligibility renders as the banner instead of modal/toast. */
  demotedToBanner: boolean;
  /** PostHog variant bucketed on first eligibility — sticky across sessions. */
  variantBucket: string | null;
};

type PromoActions = {
  /** Records a dismiss / engagement-with-the-prompt event. CTA, View-all-plans
   *  and explicit dismiss all call this; the cadence (`INDIVIDUAL_REPROMPT_DAYS`)
   *  decides how long until the next prompt. */
  recordDismissal: () => void;
  recordDemotion: () => void;
  recordVariantBucket: (variant: string) => void;
};

const INITIAL_STATE: PromoState = {
  lastDismissedAt: null,
  dismissCount: 0,
  demotedToBanner: false,
  variantBucket: null,
};

export const usePromoStore = create<PromoState & PromoActions>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,
      recordDismissal: () =>
        set((prev) => ({
          lastDismissedAt: new Date().toISOString(),
          dismissCount: prev.dismissCount + 1,
        })),
      recordDemotion: () => set({ demotedToBanner: true }),
      recordVariantBucket: (variant) => set({ variantBucket: variant }),
    }),
    {
      name: StorageKeys.UPGRADE_PROMO_STATE,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        lastDismissedAt: state.lastDismissedAt,
        dismissCount: state.dismissCount,
        demotedToBanner: state.demotedToBanner,
        variantBucket: state.variantBucket,
      }),
    },
  ),
);
